import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { styled } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';

import { useAudioPlayer } from './audioPlayerContext';

interface AudioPlayerProps {
  src: string | string[];
}

interface PlayPauseButtonProps {
  disabled?: boolean;
}

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  background: 'var(--Neutral-White-BG-100, #E7E7E7)',
  '& .MuiLinearProgress-bar': {
    background: theme.palette.teal.main,
  },
}));

function PlayPauseButton({ disabled = false }: PlayPauseButtonProps) {
  const { togglePlayPause, playing } = useAudioPlayer();

  return (
    <IconButton
      disabled={disabled}
      onClick={togglePlayPause}
      sx={{
        color: '#FFFFFF',
        background: (theme) => theme.palette.teal.main,
      }}
      size='small'
    >
      {playing ? <PauseIcon /> : <PlayArrowIcon />}
    </IconButton>
  );
}

export default function AudioPlayer({ src }: AudioPlayerProps) {
  const {
    loading,
    currentTime,
    endTime,
    position,
    player,
    secondsToTime,
    initializeAudioPlayer,
    unmountAudioPlayer,
  } = useAudioPlayer();

  useEffect(() => {
    initializeAudioPlayer();

    return () => {
      unmountAudioPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, src]);

  return (
    <Stack direction={'row'} alignItems='center' gap={3} paddingY={1}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={player} hidden={true}>
        {Array.isArray(src) ? (
          src.map((srcURL) => <source key={srcURL} src={srcURL} />)
        ) : (
          <source src={src} />
        )}
      </audio>
      <Stack>
        <PlayPauseButton disabled={loading} />
      </Stack>
      <Stack>
        <Typography>
          {`${secondsToTime(currentTime)}/${secondsToTime(endTime)}`}
        </Typography>
      </Stack>
      <Stack flexGrow={1} justifyContent='center'>
        {loading ? (
          <StyledLinearProgress />
        ) : (
          <>
            <StyledLinearProgress variant='determinate' value={position} />
          </>
        )}
      </Stack>
    </Stack>
  );
}
